import UserSelect from '@/components/UserSelect';
import {
  BusinessLinesNames,
  getBusinessTypeOptionsByLine,
} from '@/constants/business';
import { useBusinessSubTypeOptions } from '@/hooks/useBusinessSubTypeOptions';
import type { OurContactRuleBatchResult } from '@/services/customerOurContactRule';
import { batchSaveOurContactRules } from '@/services/customerOurContactRule';
import { Alert, App, Form, List, Select, Tabs } from 'antd';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import ConfigExcelImportPanel from './ConfigExcelImportPanel';
import { CUSTOMER_CONFIG_EXCEL } from './excelCodes';
import type {
  BatchScenarioPanelHandle,
  BatchScenarioPanelProps,
} from './types';
import { filterValidCustomers } from './types';

/** 批量设置同一场景联系人或导入各客户场景，仅更新场景表，不修改默认联系人及客户机构。 */
const OurContactBatchPanel = forwardRef<
  BatchScenarioPanelHandle,
  BatchScenarioPanelProps
>(({ customers, active, onDirty, onFooterChange }, ref) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [mode, setMode] = useState('uniform');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<OurContactRuleBatchResult>();
  const line = Form.useWatch('businessLines', form);
  const type = Form.useWatch('businessType', form);
  const { options, loading } = useBusinessSubTypeOptions({
    businessLines: line,
    businessType: type,
  });

  useEffect(() => {
    if (!active) return;
    form.resetFields();
    setMode('uniform');
    setResult(undefined);
  }, [active, form]);

  useEffect(() => {
    if (active) onFooterChange({ showOk: mode === 'uniform', submitting });
  }, [active, mode, submitting, onFooterChange]);

  useImperativeHandle(ref, () => ({
    /** 校验统一场景后提交所选客户；部分失败保留表单和明细供修正。 */
    submit: async () => {
      if (mode !== 'uniform' || submitting) return false;
      try {
        const values = await form.validateFields();
        const selected = filterValidCustomers(customers);
        if (!selected.length) throw new Error('请选择客户');
        if (selected.length > 200)
          throw new Error('单次最多修改200个客户，请分批操作');
        setSubmitting(true);
        setResult(undefined);
        const response = await batchSaveOurContactRules(
          selected.map((customer) => ({
            customerId: String(customer.id),
            businessLines: values.businessLines,
            businessType: values.businessType ?? 0,
            businessSubTypeId: values.businessSubTypeId ?? '0',
            userId: values.userId,
          })),
        );
        if (!response.success || !response.result)
          throw new Error(response.message || '批量编辑联系人场景失败');
        setResult(response.result);
        if (response.result.successCount > 0) onDirty();
        if (response.result.failedCount > 0) {
          message.warning(
            `批量编辑完成：成功 ${response.result.successCount} 条，失败 ${response.result.failedCount} 条`,
          );
          return false;
        }
        message.success(
          `已成功修改 ${response.result.successCount} 个客户的联系人场景`,
        );
        return true;
      } catch (error) {
        if (error && typeof error === 'object' && 'errorFields' in error)
          return false;
        message.error(
          error instanceof Error ? error.message : '批量编辑联系人场景失败',
        );
        return false;
      } finally {
        setSubmitting(false);
      }
    },
  }));

  return (
    <Tabs
      activeKey={mode}
      onChange={setMode}
      items={[
        {
          key: 'uniform',
          label: '统一修改',
          children: (
            <>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="相同场景更新联系人，不存在则新增。其他场景、默认联系人及客户所属机构不变，仅影响新委托。"
              />
              <Form form={form} layout="vertical" disabled={submitting}>
                <Form.Item
                  label="业务领域"
                  name="businessLines"
                  rules={[{ required: true, message: '请选择业务领域' }]}
                >
                  <Select
                    options={Object.entries(BusinessLinesNames)
                      .filter(([code]) => Number(code) > 0)
                      .map(([code, label]) => ({ value: Number(code), label }))}
                    onChange={() =>
                      form.setFieldsValue({
                        businessType: undefined,
                        businessSubTypeId: undefined,
                      })
                    }
                  />
                </Form.Item>
                <Form.Item label="业务类型" name="businessType">
                  <Select
                    allowClear
                    placeholder="不限"
                    disabled={submitting || !line}
                    options={getBusinessTypeOptionsByLine(line)}
                    onChange={() =>
                      form.setFieldValue('businessSubTypeId', undefined)
                    }
                  />
                </Form.Item>
                <Form.Item label="子业务类型" name="businessSubTypeId">
                  <Select
                    allowClear
                    placeholder="不限"
                    disabled={submitting || !type}
                    loading={loading}
                    options={options}
                  />
                </Form.Item>
                <Form.Item
                  label="我方联系人"
                  name="userId"
                  rules={[{ required: true, message: '请选择我方联系人' }]}
                >
                  <UserSelect
                    placeholder="请选择我方联系人"
                    disabled={submitting}
                  />
                </Form.Item>
              </Form>
              {result && result.failedCount > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  message={`成功 ${result.successCount} 条，失败 ${result.failedCount} 条`}
                  description={
                    <List
                      size="small"
                      dataSource={result.failedItems}
                      renderItem={(failure) => (
                        <List.Item>
                          客户 {failure.item.customerId}：{failure.reason}
                        </List.Item>
                      )}
                    />
                  }
                />
              )}
            </>
          ),
        },
        {
          key: 'rules',
          label: '场景规则导入',
          children: (
            <ConfigExcelImportPanel
              {...CUSTOMER_CONFIG_EXCEL.ourContactRule}
              description="按文件中的客户ID设置场景联系人，不受当前勾选范围限制。相同场景更新，其他场景保留；同一客户任一行失败则整组回滚。"
              onSuccess={onDirty}
            />
          ),
        },
      ]}
    />
  );
});

OurContactBatchPanel.displayName = 'OurContactBatchPanel';
export default OurContactBatchPanel;
